import { useNavigate } from 'react-router-dom'
import AppDropdown from '../../../components/AppDropdown'
import AppDateInput from '../../../components/AppDateInput'
import AppPagination from '../../../components/AppPagination'
import AppActionMenu from '../../../components/AppActionMenu'
import AppCombobox from '../../../components/AppCombobox'
import SharedFilters from '../../../components/SharedFilters'
import DateModifiedIcon from '../../../components/DateModifiedIcon'
import { useTaskListScreen } from './hooks/useTaskListScreen'

function statusBadgeClass(status) {
  if (status === 'IN_PROGRESS') return 'bg-primary-fixed text-primary'
  if (status === 'COMPLETED') return 'bg-emerald-100 text-emerald-700'
  if (status === 'CLOSED') return 'bg-surface-container-high text-on-surface-variant'
  return 'bg-surface-container text-on-surface-variant'
}

function priorityBadgeClass(priority) {
  if (priority === 'HIGH') return 'bg-error-container text-error'
  if (priority === 'LOW') return 'bg-surface-container-high text-on-surface-variant'
  return 'bg-secondary-container text-on-secondary-container'
}

function slaTextClass(state) {
  if (state === 'completed') return 'text-emerald-700'
  if (state === 'overdue') return 'text-error'
  if (state === 'due_today') return 'text-primary'
  return 'text-on-surface-variant'
}

function rowTintClass(status, slaState) {
  if (slaState === 'overdue') return 'bg-error-container/20'
  if (status === 'CLOSED') return 'bg-surface-container/30'
  return 'bg-surface-container-lowest'
}

function initials(name) {
  return String(name || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

export default function TaskLedgerScreen() {
  const { state, actions } = useTaskListScreen()
  const navigate = useNavigate()

  return (
    <div className="mx-auto w-full max-w-[1320px] pb-8">
      <header className="mb-8">
        <div className="mb-6 flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-primary">Operational Console</p>
            <h1 className="text-4xl font-extrabold tracking-tight text-on-surface md:text-5xl">Task Engine</h1>
          </div>
        </div>

        <div className="mb-6">
          <SharedFilters
            searchField={state.searchField}
            setSearchField={actions.setSearchField}
            searchFieldOptions={state.searchFieldOptions}
            searchText={state.searchText}
            setSearchText={actions.setSearchText}
            statusFilter={state.statusFilter}
            setStatusFilter={actions.setStatusFilter}
            statusOptions={state.statusOptions}
            projectFilter={state.projectFilter}
            setProjectFilter={actions.setProjectFilter}
            projectOptions={state.projectOptions}
            agentFilter={state.assigneeFilter}
            setAgentFilter={actions.setAssigneeFilter}
            agentOptions={state.userOptions}
            companyFilter={state.companyFilter}
            setCompanyFilter={actions.setCompanyFilter}
            companyOptions={state.companyOptions}
            branchFilter={state.branchFilter}
            setBranchFilter={actions.setBranchFilter}
            branchOptions={state.branchOptions}
            departmentFilter={state.departmentFilter}
            setDepartmentFilter={actions.setDepartmentFilter}
            departmentOptions={state.departmentOptions}
            priorityFilter={state.priorityFilter}
            setPriorityFilter={actions.setPriorityFilter}
            priorityOptions={state.priorityOptions}
            slaStateFilter={state.slaFilter}
            setSlaStateFilter={actions.setSlaFilter}
            slaStateOptions={state.slaOptions}
            fromDate={state.fromDate}
            setFromDate={actions.setFromDate}
            toDate={state.toDate}
            setToDate={actions.setToDate}
            onReset={actions.onReset}
            tools={[
              { key: 'export_csv', label: 'Export Tasks (CSV)', icon: 'file_download' },
              { key: 'bulk_upload', label: 'Bulk Upload', icon: 'upload_file' },
            ]}
            onToolSelect={(key) => {
              if (key === 'export_csv') {
                actions.exportTasksToCSV?.()
                return
              }
              if (key === 'bulk_upload') {
                navigate('/nerve/task/bulk-upload', {
                  state: { returnTo: `/nerve/task/list${window.location.search}` },
                })
              }
            }}
            visibleAccordionFilters={[
              'project',
              'company',
              'branch',
              'department',
              'priority',
              'slaState',
              'fromDate',
              'toDate',
            ]}
          />
        </div>
      </header>

      {state.error ? (
        <div className="mb-5 rounded-xl border border-error/30 bg-error-container px-4 py-3 text-sm text-error">{state.error}</div>
      ) : null}

      <section className="overflow-hidden rounded-[2rem] bg-surface-container-lowest">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1120px] border-collapse text-left">
            <thead>
              <tr className="border-b border-outline-variant/15 bg-surface-container-low">
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Code</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Task Name</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Project</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Status</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Priority</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Due / SLA</th>
                <th className="px-6 py-4 text-xs font-black uppercase tracking-[0.14em] text-on-surface-variant">Assigned</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>

            <tbody className="divide-y divide-surface-container">
              {state.isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    Loading tasks...
                  </td>
                </tr>
              ) : state.rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center text-sm text-on-surface-variant">
                    No tasks found for selected filters.
                  </td>
                </tr>
              ) : (
                state.rows.map((row) => (
                  <tr
                    key={row.id}
                    tabIndex={0}
                    role="link"
                    onClick={() => navigate(`/nerve/task/entry?id=${row.id}`, { state: { returnTo: `/nerve/task/list${window.location.search}` } })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        navigate(`/nerve/task/entry?id=${row.id}`, { state: { returnTo: `/nerve/task/list${window.location.search}` } })
                        return
                      }
                    }}
                    className={`${rowTintClass(row.status, row.sla.state)} cursor-pointer transition-colors hover:bg-surface-container-low focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50`}
                  >
                    <td className="px-6 py-5 align-top">
                      <span className="text-base font-black tracking-tight text-on-surface-variant">{row.taskCode}</span>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <p className="text-base font-bold leading-tight text-on-surface">{row.taskName}</p>
                      <p className="mt-1 text-xs text-on-surface-variant">{row.taskDescription || '-'}</p>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <div className="inline-flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                        <span className="text-sm font-semibold text-on-surface">{row.projectName}</span>
                      </div>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <span className={`inline-flex rounded-full px-3 py-1 text-sm font-black ${statusBadgeClass(row.status)}`}>
                        {row.status.replaceAll('_', ' ')}
                      </span>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${priorityBadgeClass(row.priority)}`}>
                        {row.priority}
                      </span>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-bold text-on-surface">{row.dueText}</p>
                        <DateModifiedIcon
                          hasDateChanges={row.hasDateChanges}
                          module="TASK"
                          entityId={row.id}
                        />
                      </div>
                      <p className={`mt-1 text-xs font-bold ${slaTextClass(row.sla.state)}`}>{row.sla.label}</p>
                    </td>

                    <td className="px-6 py-5 align-top">
                      <div className="flex -space-x-2">
                        {row.assignedUsers.slice(0, 3).map((user) => (
                          <span
                            key={`${row.id}-${user.id}`}
                            className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border-2 border-surface-container-lowest bg-primary-fixed text-[10px] font-black text-primary"
                            title={user.name || 'User'}
                          >
                            {user.avatarUrl ? (
                              <img src={user.avatarUrl} alt={user.name || 'User'} className="h-full w-full object-cover" />
                            ) : (
                              initials(user.name)
                            )}
                          </span>
                        ))}
                        {row.assignedCount > 3 ? (
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-2 border-surface-container-lowest bg-surface-container-high text-xs font-black text-on-surface-variant">
                            +{row.assignedCount - 3}
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-6 py-5 align-top text-right" onClick={(event) => event.stopPropagation()}>
                      <AppActionMenu
                        triggerLabel=""
                        triggerIcon="more_vert"
                        showChevron={false}
                        triggerClassName="h-8 w-8 rounded-md bg-transparent p-0 text-on-surface-variant hover:bg-surface-container-high hover:text-primary"
                        menuClassName="min-w-[176px]"
                        items={[
                          { key: 'edit_task', label: 'Edit Task', icon: 'edit' },
                          { key: 'add_job', label: 'Add Job', icon: 'post_add' },
                        ]}
                        onSelect={(key) => {
                          if (key === 'edit_task') {
                            navigate(`/nerve/task/entry?id=${row.id}`, { state: { returnTo: `/nerve/task/list${window.location.search}` } })
                            return
                          }
                          if (key === 'add_job') {
                            const params = new URLSearchParams({
                              taskId: String(row.id),
                              taskName: String(row.taskName || ''),
                              taskCode: String(row.taskCode || ''),
                              projectName: String(row.projectName || ''),
                            })
                            navigate(`/nerve/job/entry?${params.toString()}`)
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 border-t border-outline-variant/15 bg-surface-container-low px-6 py-4">
          <p className="text-sm font-medium text-on-surface-variant">
            Showing {state.rangeStart} - {state.rangeEnd} of {state.totalCount} active tasks
          </p>

          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.goToPage}
            onPrevious={actions.goToPreviousPage}
            onNext={actions.goToNextPage}
            canPrevious={state.page > 1}
            canNext={Boolean(state.hasMore) || state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}
